/**
 * Certificates Section
 *
 * Lists all certificates a user has earned. Shown on the Profile page
 * sidebar. Admins viewing another user's profile see the same data via
 * `getAccountCertificates(undefined, userId)`; learners viewing their
 * own profile use `getMyCertificates()` so the API can scope on the
 * authenticated user.
 *
 * Each row offers a one-click download via the SAS-signed URL on the
 * certificate's stored image filename.
 */

import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { certificatesClient, coursesClient } from '../../apis/client-instances';
import { Certificate } from '../../apis/api-client';
import { CertificateIcon, DownloadIcon } from '../icons';
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '../ui';
import { EmptyState, SectionHeader } from '../common';
import { alert, getFileUrl } from '../../hooks/function';
import { formatDate } from '../../utils/roleHelpers';

// `formatDate` takes a Date — the API gives ISO strings. Tiny adapter.
const fmt = (s: string | Date | null | undefined): string => (s ? formatDate(new Date(s)) : '');

interface CertificatesSectionProps {
  /** User whose certificates to load. */
  userId: number;
  /** True when the profile being viewed is the signed-in user's own. */
  isOwnProfile: boolean;
}

export default function CertificatesSection({ userId, isOwnProfile }: CertificatesSectionProps) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [certs, setCerts] = useState<Certificate[]>([]);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        // Own profile → /my; admin viewing someone else → account scope
        // filtered by userId. Both paths return the same Certificate[]
        // shape so the rendering below stays uniform.
        const result = isOwnProfile
          ? await certificatesClient.getMyCertificates()
          : await certificatesClient.getAccountCertificates(undefined, userId);
        if (!cancelled) setCerts(result || []);
      } catch {
        if (!cancelled) {
          alert(enqueueSnackbar, 'Failed to load certificates', 'error');
          setCerts([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [userId, isOwnProfile, enqueueSnackbar]);

  const handleDownload = async (cert: Certificate) => {
    setDownloadingId(cert.id);
    try {
      const url = await getFileUrl(cert.imageFilename);
      if (!url) {
        alert(enqueueSnackbar, 'Certificate file unavailable', 'error');
        return;
      }
      const slug = (cert.courseTitle || 'course').replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase();
      const name = (cert.studentName || 'student').replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase();
      const res = await fetch(url);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `certificate-${slug}-${name}.png`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      alert(enqueueSnackbar, 'Failed to download certificate', 'error');
    } finally {
      setDownloadingId(null);
    }
  };

  // Opening the course goes through a slug lookup since the certificate
  // model carries `courseId` but the user-facing URL is slug-based.
  const openCourse = async (courseId: number) => {
    try {
      const course = await coursesClient.getCourseById(courseId);
      if (course?.slug) navigate(`/courses/${course.slug}`);
    } catch {
      alert(enqueueSnackbar, 'Failed to open course', 'error');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (certs.length === 0) {
    return (
      <EmptyState
        icon={<CertificateIcon sx={{ fontSize: 100, color: 'grey.400' }} />}
        title="No certificates yet"
        subtitle={
          isOwnProfile
            ? 'Complete a course that issues a certificate to see it here.'
            : 'This learner hasn’t earned any certificates yet.'
        }
      />
    );
  }

  return (
    <Box>
      <SectionHeader
        variant="accent"
        icon={<CertificateIcon />}
        title="Certificates"
        subtitle={`${certs.length} earned`}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2 }}>
        {certs.map((cert) => (
          <Box
            key={cert.id}
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              transition: 'border-color 120ms ease, box-shadow 120ms ease',
              '&:hover': {
                borderColor: 'primary.main',
                boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                  onClick={() => cert.courseId && openCourse(cert.courseId)}
                >
                  {cert.courseTitle || `Course #${cert.courseId}`}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Issued {fmt(cert.createdAt)}
                  {cert.completedAt && ` · Completed ${fmt(cert.completedAt)}`}
                </Typography>
              </Box>
              <Tooltip title="Download certificate">
                <IconButton
                  size="small"
                  onClick={() => handleDownload(cert)}
                  disabled={downloadingId === cert.id}
                >
                  {downloadingId === cert.id ? (
                    <CircularProgress size={18} />
                  ) : (
                    <DownloadIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
